/**
 * Business Day Utilities
 * Handles calculation of business days, excluding weekends and US holidays
 */

/**
 * US Federal Holidays for a given year
 * Returns array of Date objects for holidays
 */
export function getUSHolidays(year: number): Date[] {
  const holidays: Date[] = [];

  // New Year's Day - January 1
  holidays.push(new Date(year, 0, 1));

  // Martin Luther King Jr. Day - Third Monday in January
  holidays.push(getNthWeekdayOfMonth(year, 0, 1, 3));

  // Presidents' Day - Third Monday in February
  holidays.push(getNthWeekdayOfMonth(year, 1, 1, 3));

  // Memorial Day - Last Monday in May
  holidays.push(getLastWeekdayOfMonth(year, 4, 1));

  // Juneteenth - June 19
  holidays.push(new Date(year, 5, 19));

  // Independence Day - July 4
  holidays.push(new Date(year, 6, 4));

  // Labor Day - First Monday in September
  holidays.push(getNthWeekdayOfMonth(year, 8, 1, 1));

  // Columbus Day - Second Monday in October
  holidays.push(getNthWeekdayOfMonth(year, 9, 1, 2));

  // Veterans Day - November 11
  holidays.push(new Date(year, 10, 11));

  // Thanksgiving - Fourth Thursday in November
  holidays.push(getNthWeekdayOfMonth(year, 10, 4, 4));

  // Christmas Day - December 25
  holidays.push(new Date(year, 11, 25));

  return holidays;
}

/**
 * Get the Nth occurrence of a weekday in a month
 * @param year - Year
 * @param month - Month (0-11)
 * @param weekday - Day of week (0=Sunday, 1=Monday, etc.)
 * @param n - Which occurrence (1=first, 2=second, etc.)
 */
function getNthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): Date {
  const firstDay = new Date(year, month, 1);
  const firstWeekday = firstDay.getDay();
  
  // Calculate days until the first occurrence of the target weekday
  let daysUntilWeekday = (weekday - firstWeekday + 7) % 7;
  
  // Calculate the date of the nth occurrence
  const targetDate = 1 + daysUntilWeekday + (n - 1) * 7;
  
  return new Date(year, month, targetDate);
}

/**
 * Get the last occurrence of a weekday in a month
 * @param year - Year
 * @param month - Month (0-11)
 * @param weekday - Day of week (0=Sunday, 1=Monday, etc.)
 */
function getLastWeekdayOfMonth(year: number, month: number, weekday: number): Date {
  // Start from the last day of the month
  const lastDay = new Date(year, month + 1, 0);
  const lastDayWeekday = lastDay.getDay();
  
  // Calculate days back to the last occurrence of the target weekday
  let daysBack = (lastDayWeekday - weekday + 7) % 7;
  
  return new Date(year, month, lastDay.getDate() - daysBack);
}

/**
 * Check if a date is a weekend (Saturday or Sunday)
 */
export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6; // Sunday or Saturday
}

/**
 * Check if a date is a US federal holiday
 */
export function isUSHoliday(date: Date): boolean {
  const year = date.getFullYear();
  const holidays = getUSHolidays(year);
  
  // Compare dates (ignoring time)
  const dateStr = date.toISOString().split('T')[0];
  return holidays.some(holiday => {
    const holidayStr = holiday.toISOString().split('T')[0];
    return dateStr === holidayStr;
  });
}

/**
 * Check if a date is a business day (not weekend, not holiday)
 */
export function isBusinessDay(date: Date): boolean {
  return !isWeekend(date) && !isUSHoliday(date);
}

/**
 * Get the next business day from a given date
 * If the date is already a business day, returns the same date
 */
export function getNextBusinessDay(date: Date): Date {
  const result = new Date(date);
  
  // If already a business day, return it
  if (isBusinessDay(result)) {
    return result;
  }
  
  // Keep adding days until we find a business day
  let attempts = 0;
  while (!isBusinessDay(result) && attempts < 14) { // Max 2 weeks to find a business day
    result.setDate(result.getDate() + 1);
    attempts++;
  }
  
  return result;
}

/**
 * Add business days to a date (skipping weekends and holidays)
 * @param startDate - Starting date
 * @param businessDays - Number of business days to add
 */
export function addBusinessDays(startDate: Date, businessDays: number): Date {
  let result = new Date(startDate);
  let daysAdded = 0;
  
  while (daysAdded < businessDays) {
    result.setDate(result.getDate() + 1);
    
    if (isBusinessDay(result)) {
      daysAdded++;
    }
  }
  
  return result;
}

/**
 * Calculate suggested next review date based on cadence
 * Ensures the date falls on a business day (not weekend or US holiday)
 * @param lastReviewDate - Date of last review (or today if null)
 * @param cadence - Review frequency: monthly, quarterly, biannual, annual
 */
export function calculateNextReviewDate(
  lastReviewDate: Date | null,
  cadence: 'monthly' | 'quarterly' | 'biannual' | 'annual'
): Date {
  const baseDate = lastReviewDate || new Date();
  const suggestedDate = new Date(baseDate);
  
  // Add days based on cadence
  switch (cadence) {
    case 'monthly':
      suggestedDate.setDate(suggestedDate.getDate() + 30);
      break;
    case 'quarterly':
      suggestedDate.setDate(suggestedDate.getDate() + 90);
      break;
    case 'biannual':
      suggestedDate.setDate(suggestedDate.getDate() + 180);
      break;
    case 'annual':
      suggestedDate.setDate(suggestedDate.getDate() + 365);
      break;
  }
  
  // Adjust to next business day if needed
  return getNextBusinessDay(suggestedDate);
}

/**
 * Format a date range showing business days
 * Example: "Monday, January 15, 2024"
 */
export function formatBusinessDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Get a human-readable explanation of why a date was adjusted
 */
export function getDateAdjustmentReason(originalDate: Date, adjustedDate: Date): string | null {
  if (originalDate.toDateString() === adjustedDate.toDateString()) {
    return null; // No adjustment needed
  }
  
  if (isWeekend(originalDate)) {
    return `Adjusted from ${originalDate.toLocaleDateString()} (weekend) to next business day`;
  }
  
  if (isUSHoliday(originalDate)) {
    return `Adjusted from ${originalDate.toLocaleDateString()} (US holiday) to next business day`;
  }
  
  return `Adjusted to next available business day`;
}
